<script>
    import {page, inertia, router} from '@inertiajs/svelte'
    import SearchBar from "~/components/SearchBar.svelte";
    import Navigation from "~/components/Navigation.svelte";
    import { Button } from "~/components/ui/button";
    import { Separator } from "~/components/ui/separator";
    import * as DropdownMenu from "~/components/ui/dropdown-menu";
    import {Search, Sun, Moon, X, ChevronDown, Sparkles, SlidersHorizontal} from "@lucide/svelte";
    import SensitiveToggle from "~/components/SensitiveToggle.svelte";
    import SensitiveAgeDialog from "~/components/SensitiveAgeDialog.svelte";
    import * as Tooltip from "~/components/ui/tooltip";
    import {userUrl} from "~/components/utils";
    import {isDesktop} from "~/components/viewport";
    import {onMount} from "svelte";
    import Logo from "~/components/Logo.svelte";

    // Reactive state, not a getter: read straight from the DOM inside the
    // markup it never re-ran, so the icon kept showing the old theme.
    let light = false;
    onMount(() => {
        light = document.documentElement.getAttribute('data-theme') === 'light';
    });

    function toggleTheme() {
        const next = light ? 'dark' : 'light'
        if (next === 'dark') {
            document.documentElement.removeAttribute('data-theme')
        } else {
            document.documentElement.setAttribute('data-theme', 'light')
        }
        light = next === 'light'
        localStorage.setItem('nyanbooru-theme', next)
    }

    export let filters = {};
    filters = filters || {};
    filters.users = $page.props.current_user ? filters?.users : undefined;

    let mobileSearchOpen = false;
    let mobileSearchInput;

    // The search overlay is mobile-only markup: at md and up it hides itself,
    // but it also replaces the logo, nav and search box, so crossing the
    // breakpoint while it was open (rotating a large phone to landscape,
    // unfolding a foldable) left the bar empty. Close it at the boundary.
    $: if ($isDesktop) mobileSearchOpen = false;

    $: showMobileEnhanced = !($page.url.startsWith("/tags") && $page.props.selected);

    function toggleMobileEnhanced() {
        if (!filters) filters = {};
        if (filters.enhanced) {
            delete filters.enhanced;
        } else {
            filters.enhanced = true;
        }
        filters = filters;
        localStorage.setItem('nyanbooru-text-search-tip-dismissed', Date.now());
        const url = new URL($page.url, window.location.origin);
        router.get(url.pathname, filters);
    }

    function openMobileSearch() {
        mobileSearchOpen = true;
        setTimeout(() => mobileSearchInput?.querySelector('input')?.focus(), 0);
    }
</script>

<div class="sticky z-50 top-0 bg-background/80 backdrop-blur">
    <!-- Flex items default to min-width:auto, so without the shrink/min-w-0
         below nothing in this row gives way and the bar simply bursts the
         viewport once the reader's browser font size goes up (measured: 63px
         of overflow at 320px with a 24px root font). -->
    <div class="flex items-center justify-between gap-1 px-4 py-2 min-w-0">
        <!-- Mobile: expanded search overlay -->
        {#if mobileSearchOpen}
            <div class="flex md:hidden items-center grow gap-1" bind:this={mobileSearchInput}>
                {#key filters}
                    <SearchBar filters={filters}/>
                {/key}
                <button class="flex-shrink-0 p-2 text-muted-foreground hover:text-foreground transition-colors"
                        onclick={() => mobileSearchOpen = false}>
                    <X size={18} />
                </button>
            </div>
        {:else}
            <a use:inertia href="/" class="flex items-center gap-0.5 shrink min-w-0 hover:opacity-80 transition-opacity">
                <Logo iconClass="h-10 w-16 shrink-0" showText={false} />
            </a>
            <!-- Desktop: nav + search inline -->
            <div class="hidden md:flex grow min-w-0 items-center justify-start gap-1 ml-2">
                {#key filters}
                    <Navigation filters={filters}/>
                    <div class="flex items-center grow">
                        <SearchBar filters={filters}/>
                    </div>
                {/key}
            </div>
            <!-- Mobile: search + sparkle icons -->
            <div class="flex md:hidden items-center grow shrink-0 justify-end">
                <Button variant="ghost" size="icon" class="h-8 w-8" onclick={openMobileSearch}>
                    <Search size={16} />
                </Button>
                {#if showMobileEnhanced}
                    <Tooltip.Root>
                        <Tooltip.Trigger>
                            {#snippet child({ props })}
                                <button {...props}
                                        type="button"
                                        class="p-2 rounded-md transition-colors {filters?.enhanced
                                            ? 'text-primary bg-primary/10'
                                            : 'text-muted-foreground hover:text-foreground'}"
                                        onclick={toggleMobileEnhanced}>
                                    <Sparkles size={16} />
                                </button>
                            {/snippet}
                        </Tooltip.Trigger>
                        <Tooltip.Content>
                            <p>{filters?.enhanced ? 'Enhanced text search (on)' : 'Enhanced text search (off)'}</p>
                        </Tooltip.Content>
                    </Tooltip.Root>
                {/if}
            </div>
        {/if}
    {#if $page.props.current_user}
        <div class="flex items-center gap-1 shrink-0" class:hidden={mobileSearchOpen} class:md:flex={mobileSearchOpen}>
            <Button variant="ghost" size="icon" class="h-8 w-8" onclick={toggleTheme}>
                {#if light}
                    <Moon size={16} />
                {:else}
                    <Sun size={16} />
                {/if}
            </Button>
            <a href={userUrl($page.props.current_user)} use:inertia
               class="flex items-center justify-center w-8 h-8 rounded-full bg-primary/20 text-primary text-sm font-medium hover:bg-primary/30 transition-colors">
                {($page.props.current_user.name ?? 'U').charAt(0).toUpperCase()}
            </a>
            <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                    {#snippet child({ props })}
                        <Button {...props} variant="ghost" size="sm" class="h-8 w-6 px-0">
                            <ChevronDown size={14} />
                        </Button>
                    {/snippet}
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" class="w-44">
                    <SensitiveToggle />
                    <DropdownMenu.Separator />
                    {#if $page.props.current_user?.account_type === 'admin'}
                        <DropdownMenu.Item>
                            <a href='/admin' use:inertia class="w-full">Admin</a>
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator />
                    {/if}
                    <DropdownMenu.Item>
                        <a href='/users/sign_out' use:inertia="{{ method: 'DELETE' }}" class="w-full">Logout</a>
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu.Root>
        </div>
    {:else}
        <div class="flex items-center gap-2 shrink-0" class:hidden={mobileSearchOpen} class:md:flex={mobileSearchOpen}>
            <Button variant="ghost" size="icon" class="h-8 w-8" onclick={toggleTheme}>
                {#if light}
                    <Moon size={16} />
                {:else}
                    <Sun size={16} />
                {/if}
            </Button>
            <!-- Signed-out visitors had no menu at all; the sensitive-content
                 toggle is cookie-backed and works without an account, so it
                 needs somewhere to live. The theme button stays put. -->
            <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                    {#snippet child({ props })}
                        <Button {...props} variant="ghost" size="icon" class="h-8 w-8">
                            <SlidersHorizontal size={16} />
                        </Button>
                    {/snippet}
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" class="w-44">
                    <SensitiveToggle />
                </DropdownMenu.Content>
            </DropdownMenu.Root>
            <Button variant="outline" size="sm" href="/users/sign_in">
                Sign in
            </Button>
        </div>
    {/if}
    </div>
    <!-- Mobile: nav second row. `mx-auto` on the inner row rather than
         `justify-center` on the scroller: centred flex overflow spills equally
         both ways and the left half cannot be scrolled to, so at large font
         sizes "Posts" just disappeared. Auto margins collapse to 0 once the
         content overflows, which leaves it scrollable from the start. -->
    <div class="flex md:hidden px-4 pb-1 overflow-x-auto">
        <div class="mx-auto">
            {#key filters}
                <Navigation filters={filters}/>
            {/key}
        </div>
    </div>
</div>

<Separator />

<!-- Rendered once, at the root: a dialog inside DropdownMenu.Content is
     destroyed the moment selecting its trigger item closes the menu. -->
<SensitiveAgeDialog />
